import type { AddressLookupError } from '@t/address.types';

const isFailureKind = (value: string): value is AddressLookupError['kind'] =>
  value === 'throttled' || value === 'misconfigured' || value === 'transient';

/**
 * The proxy answers a failure with one closed-set kind and a non-2xx status, and
 * the response interceptor has already lifted that kind onto `error.name`.
 *
 * Anything else — a network failure, a cancellation, a gateway's own error page —
 * is transient: it is our side or the wire, not Novus's account.
 */
export const addressLookupFailureKind = (error: unknown): AddressLookupError['kind'] => {
  const name = error instanceof Error ? error.name : '';
  return isFailureKind(name) ? name : 'transient';
};

export const classifyProxiedLookupError = (
  error: unknown,
  phase: AddressLookupError['phase']
): AddressLookupError => ({ kind: addressLookupFailureKind(error), phase });

/**
 * At most one retransmit, and none at all for either operator fault — retrying
 * those spends the account's remaining allowance to be told the same thing twice,
 * at the exact moment the provider asked us to stop.
 *
 * Stated here and applied to every proxied lookup, because production builds its
 * query client with no default options, so the library's own `retry: 3` applies
 * to anything that does not override it.
 */
export const retryAddressLookup = (failureCount: number, error: unknown): boolean =>
  failureCount < 1 && addressLookupFailureKind(error) === 'transient';
